import {FC, useEffect, useState} from 'react';
import {toast} from 'react-toastify';
//store
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {getCurrentEventSelector} from '@/store/organization-event';
import {getAuctionCategories, getAuctionCategoriesSelector} from '@/store/organization-auction';
import {getAttendeeSelector} from '@/store/organization-attendees';
//mui
import {
	Box,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
	Typography,
} from '@mui/material';
//constants
import {
	ACTIVITY,
	ALL_CATEGORIES,
	AMOUNT,
	AUCTION,
	BID,
	CAPTION,
	CATEGORIES,
	CENTER,
	DOLLAR,
	HASH,
	ID,
	IMAGE_TABLE_ALT,
	ITEM,
	RIGHT,
	SUBTITLE1,
	SUBTITLE2,
	TABLE,
	TABLE_NAME,
	TABLE_NAME_CAMEL,
	TABLE_NUMBER_CAMEL,
	WINNER,
	defaultPage,
	defaultPageSize,
} from '@/utils/constants';
//helpers
import {camelize, getErrorMessage, getTableSortChanges} from '@/utils/helpers';
import {
	attendeeBidsDefaultFilterValues,
	attendeeBidsDefaultFilterData,
	getUpdatedFilterData,
	handleSetCategoryIds,
	updatedAttendeeBidsFilterOptions,
} from './helpers';
//icons
import {ReactComponent as UpDownIcon} from '@/assets/icons/arrow-up-down.svg';
import {ReactComponent as MedalIcon} from '@/assets/icons/medal-star.svg';
//images
import TestImage from '@/assets/images/test-table-image.png';
//types
import {IFilterGroup, IOrganizationAttendeeBidsResponse, IOrganizationAuctionCategory} from '@/utils/types';
//services
import {OrganizationAttendeesService} from '@/utils/services';
//components
import Loader from '@/shared/components/Loader';
import SearchHeader from '@/shared/components/Table/SearchHeader';
import Pagination from '@/shared/components/Pagination';
import EmptyTableData from '@/shared/components/Table/EmptyTableData';
//styles
import {attendeeBidsDetailsTabStyle} from './AttendeeBidsDetailsTab.styles';
import {orgAPI} from '@/core/api';

const AttendeeBidsDetailsTab: FC = () => {
	const dispatch = useAppDispatch();

	const [attendeeBidsItems, setAttendeeBidsItems] = useState<IOrganizationAttendeeBidsResponse | null>(null);

	const [isDataLoaded, setIsDataLoaded] = useState<boolean>(false);
	const [page, setPage] = useState<number>(defaultPage);
	const [searchName, setSearchName] = useState<string>('');

	const [filterData, setFilterData] = useState<IFilterGroup[]>(attendeeBidsDefaultFilterData);
	const [filterValues, setFilterValues] = useState(attendeeBidsDefaultFilterValues);

	const currentEvent = useAppSelector(getCurrentEventSelector);
	const categories = useAppSelector(getAuctionCategoriesSelector);
	const attendee = useAppSelector(getAttendeeSelector);

	useEffect(() => {
		if (currentEvent) getAttendeeBidsItems(searchName);
	}, [currentEvent, page, filterValues]);

	useEffect(() => {
		if (!categories) getCategoriesData();
	}, []);

	const getCategoriesData = async () => {
		try {
			await dispatch(getAuctionCategories(orgAPI)).unwrap();
		} catch (error) {
			toast.error(getErrorMessage(error));
		}
	};

	useEffect(() => {
		if (categories) setFilterData(updatedAttendeeBidsFilterOptions(categories, filterData));
	}, [categories]);

	const handleSortChange = (newSortColumn: string) => () =>
		getTableSortChanges(newSortColumn, filterValues, setFilterValues);

	const updateFilterData = (groupTitle: string, optionTitle: string, id?: number) => {
		if (optionTitle === ALL_CATEGORIES) {
			return setFilterData(updatedAttendeeBidsFilterOptions(categories as IOrganizationAuctionCategory[], filterData));
		}

		return setFilterData((prevFilterData) => getUpdatedFilterData(prevFilterData, groupTitle, optionTitle, id));
	};

	const getAttendeeBidsItems = async (value?: string) => {
		setIsDataLoaded(false);

		try {
			const reqBody = {
				bidderId: attendee!.id,
				page,
				searchName: value,
				...filterValues,
			};

			// TODO: to check with BE to see why 400 returns
			const attendeeBids = await OrganizationAttendeesService.getAttendeeBids(reqBody);
			setAttendeeBidsItems(attendeeBids.data);
		} catch (error) {
			toast.error(getErrorMessage(error));
		} finally {
			setIsDataLoaded(true);
		}
	};

	const handlePaginationChange = (step: number) => {
		setFilterValues((prevState) => ({
			...prevState,
			isOrderDesc: false,
			sortColumn: '',
		}));

		setPage(step);
	};

	const handleSearchChange = (value: string) => {
		setSearchName(value);
		getAttendeeBidsItems(value);
	};

	const handlePressEnter = () => {
		setPage(defaultPage);
		if (currentEvent) getAttendeeBidsItems(searchName);
	};

	const handleFilterChange = (groupTitle: string, optionTitle: string, id?: number) => {
		updateFilterData(groupTitle, optionTitle, id);

		switch (groupTitle) {
			case ACTIVITY:
				setFilterValues((prevState) => ({
					...prevState,
					activity: camelize(optionTitle),
				}));
				break;

			case AUCTION:
				setFilterValues((prevState) => ({
					...prevState,
					auctionType: camelize(optionTitle),
				}));
				break;

			case CATEGORIES:
				const newCategoryIds = handleSetCategoryIds(filterValues.categoryIds, id);
				setFilterValues({
					...filterValues,
					categoryIds: newCategoryIds as null,
				});

				break;

			default:
				break;
		}
	};

	return (
		<Box sx={attendeeBidsDetailsTabStyle.wrapper}>
			<SearchHeader
				page={page}
				items={defaultPageSize}
				total={attendeeBidsItems?.totalCount}
				parentClassName={attendeeBidsDetailsTabStyle.search}
				onPressEnter={handlePressEnter}
				onSearchChange={handleSearchChange}
				onFilterChange={handleFilterChange}
				filterOption={filterData}
			/>

			{isDataLoaded ? (
				attendeeBidsItems?.totalCount ? (
					<Box sx={attendeeBidsDetailsTabStyle.tableContent}>
						<TableContainer sx={attendeeBidsDetailsTabStyle.container}>
							<Table sx={attendeeBidsDetailsTabStyle.table} stickyHeader>
								<TableHead>
									<TableRow sx={attendeeBidsDetailsTabStyle.tableHeader}>
										<TableCell>
											<TableSortLabel active onClick={handleSortChange(ID)} IconComponent={UpDownIcon}>
												{BID}
												{HASH}
											</TableSortLabel>
										</TableCell>

										<TableCell>{ITEM}</TableCell>

										<TableCell>{AUCTION}</TableCell>

										<TableCell>
											<TableSortLabel active onClick={handleSortChange(TABLE_NUMBER_CAMEL)} IconComponent={UpDownIcon}>
												{TABLE}
												{HASH}
											</TableSortLabel>
										</TableCell>

										<TableCell>
											<TableSortLabel active onClick={handleSortChange(TABLE_NAME_CAMEL)} IconComponent={UpDownIcon}>
												{TABLE_NAME}
											</TableSortLabel>
										</TableCell>

										<TableCell align={RIGHT}>
											<TableSortLabel active onClick={handleSortChange(AMOUNT)} IconComponent={UpDownIcon}>
												{AMOUNT}
											</TableSortLabel>
										</TableCell>

										<TableCell align={CENTER}>{WINNER}</TableCell>
									</TableRow>
								</TableHead>

								<TableBody>
									{attendeeBidsItems?.data.map((row) => (
										<TableRow key={row.id} sx={attendeeBidsDetailsTabStyle.tableBody}>
											<TableCell>
												{HASH}
												{row.id}
											</TableCell>

											<TableCell>
												<Box sx={attendeeBidsDetailsTabStyle.bodyCell}>
													<Box sx={attendeeBidsDetailsTabStyle.bodyCellImage}>
														<img
															src={row.photo ?? TestImage}
															alt={IMAGE_TABLE_ALT}
															style={{width: '48px', height: '48px', borderRadius: '4px'}}
														/>
													</Box>
													<Box sx={attendeeBidsDetailsTabStyle.bodyCellImageContent}>
														<Typography variant={SUBTITLE1}>{row.itemName}</Typography>
														<Typography variant={CAPTION}>{row.itemCategory}</Typography>
													</Box>
												</Box>
											</TableCell>

											<TableCell>{row.auctionType}</TableCell>

											<TableCell>{row.tableNumber}</TableCell>

											<TableCell>
												<Typography variant={SUBTITLE2}>{row.tableName}</Typography>
												<Typography variant={CAPTION}>{row.tableTier}</Typography>
											</TableCell>

											<TableCell align={RIGHT}>
												{DOLLAR}
												{row.amount}
											</TableCell>

											<TableCell align={CENTER}>{row.isWinning && <MedalIcon />}</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>

						<Box sx={attendeeBidsDetailsTabStyle.pagination}>
							<Pagination
								page={page}
								pageSize={attendeeBidsItems.pageSize}
								total={attendeeBidsItems.totalCount}
								onChange={(step) => handlePaginationChange(step)}
							/>
						</Box>
					</Box>
				) : (
					<EmptyTableData />
				)
			) : (
				<Loader parentClassName={attendeeBidsDetailsTabStyle.loader} size={50} />
			)}
		</Box>
	);
};

export default AttendeeBidsDetailsTab;
